// API utility functions with authentication

// Check if we're in production
const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (isProduction ? 'https://ST Studio.onrender.com/api' : 'http://localhost:5050/api');

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Internal: set token after refresh
const setAccessToken = (token) => {
  if (!token) return;
  // Prefer preserving the original storage choice
  if (localStorage.getItem('token')) {
    localStorage.setItem('token', token);
  } else if (sessionStorage.getItem('token')) {
    sessionStorage.setItem('token', token);
  } else {
    // Default to session if none
    sessionStorage.setItem('token', token);
  }
};

// Wrapper for authenticated fetch requests
export const authFetch = async (url, options = {}) => {
  const headers = {
    ...getAuthHeaders(),
    ...options.headers
  };

  let response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include' // allow cookies for refresh endpoint
  });

  // If unauthorized (401), try refresh once
  if (response.status === 401) {
    try {
      const refreshRes = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (refreshRes.ok) {
        const data = await refreshRes.json();
        setAccessToken(data.token);
        // Retry original request with new token
        const retryHeaders = {
          ...headers,
          'Authorization': `Bearer ${data.token}`
        };
        response = await fetch(url, {
          ...options,
          headers: retryHeaders,
          credentials: 'include'
        });
      } else {
        // Refresh failed → clear and redirect
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
    } catch (e) {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      window.location.href = '/login';
      return;
    }
  }

  if (response.status === 403) {
    console.log('Forbidden - User authenticated but lacks permission');
  }

  return response;
};

// API methods
export const api = {
  // Customers
  customers: {
    getAll: () => authFetch(`${API_BASE_URL}/customers`),
    create: (data) => authFetch(`${API_BASE_URL}/customers`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'DELETE'
    })
  },

  // Bookings
  bookings: {
    getAll: () => authFetch(`${API_BASE_URL}/bookings`),
    getById: (id) => authFetch(`${API_BASE_URL}/bookings/${id}`),
    create: (data) => authFetch(`${API_BASE_URL}/bookings`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'DELETE'
    })
  },

  // Timeline
  timeline: {
    get: (bookingId) => authFetch(`${API_BASE_URL}/timeline/${bookingId}`),
    addEvent: (bookingId, data) => authFetch(`${API_BASE_URL}/timeline/${bookingId}/events`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    updateEvent: (bookingId, eventId, data) => authFetch(`${API_BASE_URL}/timeline/${bookingId}/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    deleteEvent: (bookingId, eventId) => authFetch(`${API_BASE_URL}/timeline/${bookingId}/events/${eventId}`, {
      method: 'DELETE'
    }),
    getActivityLogs: (bookingId) => authFetch(`${API_BASE_URL}/timeline/${bookingId}/activities`),
    addEventNote: (bookingId, eventId, data) => authFetch(`${API_BASE_URL}/timeline/${bookingId}/events/${eventId}/notes`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  // Employees
  employees: {
    getAll: () => authFetch(`${API_BASE_URL}/employees`),
    getById: (id) => authFetch(`${API_BASE_URL}/employees/${id}`),
    create: (data) => authFetch(`${API_BASE_URL}/employees`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'DELETE'
    })
  },

  // Inventory
  inventory: {
    getAll: () => authFetch(`${API_BASE_URL}/inventory`),
    getById: (id) => authFetch(`${API_BASE_URL}/inventory/${id}`),
    create: (data) => authFetch(`${API_BASE_URL}/inventory`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/inventory/${id}`, {
      method: 'DELETE'
    }),
    getLogs: () => authFetch(`${API_BASE_URL}/inventory/logs`),
    createLog: (data) => authFetch(`${API_BASE_URL}/inventory/logs`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    deleteLog: (id) => authFetch(`${API_BASE_URL}/inventory/logs/${id}`, {
      method: 'DELETE'
    })
  },

  // Roles
  roles: {
    getAll: () => authFetch(`${API_BASE_URL}/roles`),
    getPermissions: () => authFetch(`${API_BASE_URL}/roles/permissions`),
    create: (data) => authFetch(`${API_BASE_URL}/roles`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/roles/${id}`, {
      method: 'DELETE'
    })
  },

  // Auth
  auth: {
    login: (data) => fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(data)
    }),
    getMe: () => authFetch(`${API_BASE_URL}/auth/me`)
  },

  // Settings
  settings: {
    getInventorySettings: () => authFetch(`${API_BASE_URL}/settings/inventory`),
    updateInventorySettings: (data) => authFetch(`${API_BASE_URL}/settings/inventory`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  // Invoice Templates
  invoiceTemplates: {
    getAll: () => authFetch(`${API_BASE_URL}/invoice-templates`),
    getById: (id) => authFetch(`${API_BASE_URL}/invoice-templates/${id}`),
    getDefault: () => authFetch(`${API_BASE_URL}/invoice-templates/default/current`),
    create: (data) => authFetch(`${API_BASE_URL}/invoice-templates`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/invoice-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/invoice-templates/${id}`, {
      method: 'DELETE'
    }),
    duplicate: (id) => authFetch(`${API_BASE_URL}/invoice-templates/${id}/duplicate`, {
      method: 'POST'
    })
  },

  // Packages
  packages: {
    getAll: () => authFetch(`${API_BASE_URL}/packages`),
    getById: (id) => authFetch(`${API_BASE_URL}/packages/${id}`),
    create: (data) => authFetch(`${API_BASE_URL}/packages`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/packages/${id}`, {
      method: 'DELETE'
    }),
    updateInventoryDeductions: (id, deductions) => authFetch(`${API_BASE_URL}/packages/${id}/inventory-deductions`, {
      method: 'PUT',
      body: JSON.stringify({ inventoryDeductions: deductions })
    }),
    getTimelineTemplate: (id) => authFetch(`${API_BASE_URL}/packages/${id}/timeline-template`),
    updateTimelineTemplate: (id, template) => authFetch(`${API_BASE_URL}/packages/${id}/timeline-template`, {
      method: 'PUT',
      body: JSON.stringify({ timelineTemplate: template })
    })
  },

  // Notifications
  notifications: {
    getAll: (page = 1, limit = 20, unreadOnly = false) =>
      authFetch(`${API_BASE_URL}/notifications?page=${page}&limit=${limit}&unreadOnly=${unreadOnly}`),
    getUnreadCount: () => authFetch(`${API_BASE_URL}/notifications/unread-count`),
    markAsRead: (id) => authFetch(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'PATCH'
    }),
    markAllAsRead: () => authFetch(`${API_BASE_URL}/notifications/mark-all-read`, {
      method: 'PATCH'
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/notifications/${id}`, {
      method: 'DELETE'
    }),
    deleteAll: () => authFetch(`${API_BASE_URL}/notifications/delete-all`, {
      method: 'DELETE'
    })
  },

  // Event Types
  eventTypes: {
    getAll: () => authFetch(`${API_BASE_URL}/event-types`),
    getById: (id) => authFetch(`${API_BASE_URL}/event-types/${id}`),
    create: (data) => authFetch(`${API_BASE_URL}/event-types`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    update: (id, data) => authFetch(`${API_BASE_URL}/event-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    delete: (id) => authFetch(`${API_BASE_URL}/event-types/${id}`, {
      method: 'DELETE'
    }),
    reorder: (eventTypes) => authFetch(`${API_BASE_URL}/event-types/reorder/batch`, {
      method: 'PUT',
      body: JSON.stringify({ eventTypes })
    }),
    initialize: () => authFetch(`${API_BASE_URL}/event-types/initialize`, {
      method: 'POST'
    }),
    deleteNonSystem: () => authFetch(`${API_BASE_URL}/event-types/delete-non-system`, {
      method: 'DELETE'
    }),
    removeDuplicates: () => authFetch(`${API_BASE_URL}/event-types/remove-duplicates`, {
      method: 'POST'
    })
  },

  // Package Templates
  packageTemplates: {
    createDefaultTemplates: () => authFetch(`${API_BASE_URL}/package-templates/create-default-templates`, {
      method: 'POST'
    })
  },

  // Company Info
  companyInfo: {
    get: () => authFetch(`${API_BASE_URL}/company-info`),
    update: (data) => authFetch(`${API_BASE_URL}/company-info`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    uploadLogo: (file) => {
      const formData = new FormData();
      formData.append('logo', file);
      return fetch(`${API_BASE_URL}/company-info/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        credentials: 'include',
        body: formData
      });
    },
    uploadFavicon: (file) => {
      const formData = new FormData();
      formData.append('favicon', file);
      return fetch(`${API_BASE_URL}/company-info/favicon`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        },
        credentials: 'include',
        body: formData
      });
    },
    deleteLogo: () => authFetch(`${API_BASE_URL}/company-info/logo`, {
      method: 'DELETE'
    }),
    deleteFavicon: () => authFetch(`${API_BASE_URL}/company-info/favicon`, {
      method: 'DELETE'
    })
  },

  // Deploy
  deploy: {
    triggerFrontend: () => authFetch(`${API_BASE_URL}/deploy/frontend`, {
      method: 'POST'
    }),
    getStatus: () => authFetch(`${API_BASE_URL}/deploy/status`)
  },

  // Generic methods for direct endpoint access
  get: (endpoint) => authFetch(`${API_BASE_URL}${endpoint}`),
  post: (endpoint, data) => authFetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  put: (endpoint, data) => authFetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  patch: (endpoint, data) => authFetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }),
  delete: (endpoint) => authFetch(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE'
  })
};